import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getAdminPeople, createAdminPerson, updateAdminPerson, deleteAdminPerson, createAdminAccountFor,
  getAdminAreas, getRoleOptions, importarNomina, getBukEstado, sincronizarBuk,
  getBukSyncEstado, sincronizarBukAusencias,
} from '../../api/client'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import RolesChecklist from '../../components/admin/RolesChecklist'
import { roleBadge, useRoleText } from '../../components/admin/roles'
import { detalleError } from '../../components/admin/detalle'
import PersonaPicker, { usePersonas } from '../../components/PersonaPicker'

// What Capacity groups people by (backend disciplines.DISCIPLINE_BUCKETS).
const DISCIPLINAS = ['mec', 'elect', 'software', 'factory', 'otros']

// Administración > Personas: everyone in the company, one register
// (models.TeamMember), whether or not they ever use the app. Each person has
// their areas (one of them maybe as jefe), Engineering's planning fields, and
// optionally a login account -- made here before they first sign in.

const DOMINIO = '@rebornelectric.cl'
const VACIO = {
  name: '', email: '', phone: '', rut: '', role: '', is_active: true,
  areas: [], plans_hours: false, max_weekly_hours: 40,
  supervisor_id: null, profesion: '', centro_costo: '', fecha_nacimiento: '', nacionalidad: '',
  disciplina: '', es_equipo: false,
  crearCuenta: false, roles: ['engineer'],
}
const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'
const labelCls = 'block text-xs font-medium text-gray-600 mb-1.5'

// none | pendiente (made, never signed in) | activa | inactiva
const estadoCuenta = (cuenta) =>
  !cuenta ? 'none' : !cuenta.is_active ? 'inactiva' : cuenta.ha_ingresado ? 'activa' : 'pendiente'

const CUENTA_BADGE = {
  none:      'bg-gray-100 text-gray-400',
  pendiente: 'bg-amber-100 text-amber-700',
  activa:    'bg-emerald-100 text-emerald-700',
  inactiva:  'bg-gray-200 text-gray-600',
}

export default function AdminPersonas() {
  const { t } = useTranslation(['admin', 'common'])
  const [personas, setPersonas] = useState(null)
  const [areas, setAreas] = useState([])
  const [cargos, setCargos] = useState([])
  const [error, setError] = useState('')
  const [buscar, setBuscar] = useState('')
  const [area, setArea] = useState('')
  const [inactivos, setInactivos] = useState(false)
  // '' every person | 'sin_disciplina' planned with no discipline | 'equipos' fabrication teams
  const [vista, setVista] = useState('')
  const [editando, setEditando] = useState(null)      // null | 'new' | persona
  const [importando, setImportando] = useState(null)   // null | 'excel' | 'buk'
  const [bukListo, setBukListo] = useState(false)      // the server has a Buk token

  useEffect(() => {
    getAdminAreas().then(setAreas).catch(() => {})
    getRoleOptions().then(setCargos).catch(() => {})
    getBukEstado().then(r => setBukListo(!!r.configurado)).catch(() => {})
  }, [])

  const cargar = useCallback(() => {
    const params = { include_inactive: inactivos }
    if (area) params.area = area
    if (vista === 'sin_disciplina') params.sin_disciplina = true
    params.equipos = vista === 'equipos'                 // teams only in their own view
    if (buscar.trim()) params.q = buscar.trim()
    return getAdminPeople(params)
      .then(ps => { setPersonas(ps); setError('') })
      .catch(() => setError(t('personas.loadFailed')))
  }, [area, buscar, inactivos, vista, t])

  // Typing waits a moment before asking the server.
  useEffect(() => {
    const id = setTimeout(cargar, 250)
    return () => clearTimeout(id)
  }, [cargar])

  const eliminar = async (p) => {
    if (!window.confirm(t('personas.deleteConfirm', { name: p.name }))) return
    setError('')
    try {
      await deleteAdminPerson(p.id)
      setPersonas(ps => ps.filter(x => x.id !== p.id))
    } catch (e) {
      setError(detalleError(e, t('personas.deleteFailed')))
    }
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('personas.title')} subtitle={t('personas.subtitle')}
          actions={(
            <div className="flex gap-2">
              {bukListo && <SyncAusencias t={t} />}
              {bukListo && (
                <button onClick={() => setImportando('buk')}
                  className="border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                  {t('personas.buk.button')}
                </button>
              )}
              <button onClick={() => setImportando('excel')}
                className="border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                {t('personas.nomina.button')}
              </button>
              <button onClick={() => setEditando('new')}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
                {t('personas.add')}
              </button>
            </div>
          )} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder={t('personas.buscar')}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-72 bg-white" />
        <select value={area} onChange={e => setArea(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{t('personas.todasLasAreas')}</option>
          {areas.map(a => <option key={a.id} value={a.code}>{a.name}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={inactivos} onChange={e => setInactivos(e.target.checked)} className="accent-gray-900" />
          {t('personas.mostrarInactivos')}
        </label>
        <select value={vista} onChange={e => setVista(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">{t('personas.vista.todas')}</option>
          <option value="sin_disciplina">{t('personas.vista.sinDisciplina')}</option>
          <option value="equipos">{t('personas.vista.equipos')}</option>
        </select>
        {personas && <span className="text-xs text-gray-500">{t('personas.count', { count: personas.length })}</span>}
      </div>

      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

      {!personas ? (
        !error && <p className="text-gray-400 text-sm">{t('common:actions.loading')}</p>
      ) : !personas.length ? (
        <div className="text-center py-12 text-gray-400"><p className="text-sm">{t('personas.empty')}</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['name', 'email', 'cargo', 'disciplina', 'areas', 'cuenta', 'estado'].map(c => (
                  <th key={c} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                    {t(`personas.columns.${c}`)}
                  </th>
                ))}
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {personas.map(p => {
                const ec = estadoCuenta(p.cuenta)
                return (
                  <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/60 last:border-b-0">
                    <td className={`px-5 py-3 font-medium ${p.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                      <button type="button" onClick={() => setEditando(p)} className="hover:underline text-left">{p.name}</button>
                      {p.rut && <p className="text-[11px] text-gray-400 font-normal tabular-nums">{p.rut}</p>}
                    </td>
                    <td className="px-5 py-3 text-gray-600">{p.email || <span className="text-gray-300">—</span>}</td>
                    <td className="px-5 py-3 text-gray-600">{p.role || <span className="text-gray-300">—</span>}</td>
                    <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{p.disciplina
                      ? t(`personas.disciplinas.${p.disciplina}`)
                      : <span className={p.plans_hours && !p.es_equipo ? 'text-amber-600' : 'text-gray-300'}
                          title={p.plans_hours && !p.es_equipo ? t('personas.form.sinDisciplina') : undefined}>—</span>}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {p.areas.length ? p.areas.map(a => (
                          <span key={a.area_id} title={a.es_jefe ? t('personas.jefeDe', { area: a.name }) : undefined}
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              a.es_jefe ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                            {a.name}{a.es_jefe && <span className="ml-1">★ {t('personas.jefe')}</span>}
                          </span>
                        )) : <span className="text-xs text-gray-300">—</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CUENTA_BADGE[ec]}`}>
                        {t(`personas.cuentaEstado.${ec}`)}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {p.is_active
                        ? <span className="text-xs text-emerald-600 font-medium">{t('personas.activo')}</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-600">{t('personas.inactivo')}</span>}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditando(p)} className="text-xs text-gray-400 hover:text-gray-900 mr-3">{t('common:actions.edit')}</button>
                      <button onClick={() => eliminar(p)} className="text-xs text-gray-400 hover:text-red-500">{t('common:actions.delete')}</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {editando && (
        <PersonaModal
          persona={editando === 'new' ? null : editando}
          areas={areas}
          cargos={cargos}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); cargar() }}
        />
      )}

      {importando && (
        <NominaModal
          fuente={importando}
          personas={personas || []}
          onClose={() => setImportando(null)}
          onApplied={cargar}
        />
      )}
    </div>
  )
}

// Leave and holidays from Buk: synced daily and by its webhook on their own
// (backend buk_ausencias.py); this runs it now and says how the last went.
function SyncAusencias({ t }) {
  const [estado, setEstado] = useState(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const cargar = () => getBukSyncEstado().then(setEstado).catch(() => {})
  useEffect(() => { cargar() }, [])

  const correr = async () => {
    setBusy(true); setMsg('')
    try {
      const r = await sincronizarBukAusencias()
      setMsg(t('personas.bukAusencias.listo', { nuevas: r.nuevas, actualizadas: r.actualizadas, canceladas: r.canceladas }))
    } catch (e) {
      setMsg(detalleError(e, t('personas.bukAusencias.failed')))
    }
    setBusy(false)
    cargar()
  }

  const ultima = estado?.ultima ? new Date(estado.ultima + 'Z').toLocaleString() : t('personas.bukAusencias.nunca')
  const titulo = [t('personas.bukAusencias.ultima', { cuando: ultima }), estado?.mensaje, msg].filter(Boolean).join('\n')
  return (
    <button onClick={correr} disabled={busy} title={titulo}
      className={`border bg-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 ${
        estado?.ok === false ? 'border-amber-300 text-amber-700' : 'border-gray-200 text-gray-700'}`}>
      {busy ? t('personas.nomina.working') : t('personas.bukAusencias.button')}
    </button>
  )
}

// ── Importar nómina ─────────────────────────────────────────────────────────
// HR's nómina is the source of truth for names, emails and RUTs. Upload it,
// preview what it would change (the server rolls it back), then apply the
// same file -- like the monday.com import in Configuración > Sistema.

const NOMINA_CLAVES = ['actualizadas', 'creadas', 'sin_cambio', 'ambiguas', 'conflictos', 'errores', 'no_en_nomina',
  'inactivas_omitidas', 'supervisores', 'supervisores_sin_persona', 'areas_nuevas', 'areas_desactivadas']
// Rows the import skips or can't settle: worth a look before applying.
const NOMINA_ALERTAS = new Set(['ambiguas', 'conflictos', 'errores', 'supervisores_sin_persona'])

function NominaResumen({ filas, resumen, nombres, conFila = true }) {
  const { t } = useTranslation('admin')
  const campo = (c) => t(`personas.nomina.campo.${c}`, { defaultValue: c })
  const vacio = (v) => v == null || v === ''
  // A row number means something in a file, not in Buk.
  const enFila = (x) => conFila ? <span className="text-gray-400">{t('personas.nomina.fila', { fila: x.fila })}</span> : null
  const item = {
    actualizadas: (x) => (
      <>
        {enFila(x)}{' '}
        <span className="font-medium text-gray-700">
          {x.nombre ?? nombres[x.persona_id] ?? `#${x.persona_id}`}
        </span>
        <ul className="ml-4 mt-0.5 space-y-0.5">
          {Object.entries(x.cambios).map(([c, [antes, despues]]) => (
            <li key={c}>
              {campo(c)}:{' '}
              <span className={vacio(antes) ? 'text-gray-300' : 'line-through text-gray-400'}>{vacio(antes) ? '—' : antes}</span>
              {' → '}
              <span className="text-gray-800">{vacio(despues) ? '—' : despues}</span>
            </li>
          ))}
        </ul>
      </>
    ),
    creadas: (x) => (
      <>
        {enFila(x)}{' '}
        <span className="font-medium text-gray-700">{x.nombre}</span>
        {x.email && <span className="text-gray-500"> · {x.email}</span>}
      </>
    ),
    ambiguas: (x) => (
      <>
        {enFila(x)}{' '}
        <span className="font-medium text-gray-700">{x.nombre}</span>
        <span className="text-gray-500"> — {t('personas.nomina.candidatos')}: {(x.candidatos || []).join(' · ') || '—'}</span>
      </>
    ),
  }

  return (
    <dl className="divide-y divide-gray-100 border border-gray-200 rounded-lg text-sm">
      <div className="px-3 py-2 flex justify-between gap-4">
        <span className="text-gray-600">{t('personas.nomina.keys.filas')}</span>
        <span className="font-medium text-gray-900">{filas}</span>
      </div>
      {NOMINA_CLAVES.map(k => {
        const v = resumen[k]
        const lista = Array.isArray(v)
        const n = lista ? v.length : (v || 0)
        const alerta = NOMINA_ALERTAS.has(k) && n > 0
        const label = <span className={alerta ? 'text-amber-800' : 'text-gray-600'}>{t(`personas.nomina.keys.${k}`)}</span>
        return (
          <div key={k} className={`px-3 py-2 ${alerta ? 'bg-amber-50' : ''}`}>
            {lista && n > 0 ? (
              <details>
                <summary className="cursor-pointer flex justify-between gap-4">
                  {label}
                  <span className="font-medium text-gray-900">{n}</span>
                </summary>
                {k === 'no_en_nomina' && (
                  <p className="mt-2 text-xs text-gray-500">{t('personas.nomina.noEnNominaHint')}</p>
                )}
                <ul className="mt-2 max-h-56 overflow-y-auto text-xs text-gray-600 space-y-1">
                  {v.map((x, i) => <li key={i}>{item[k] ? item[k](x) : String(x)}</li>)}
                </ul>
              </details>
            ) : (
              <div className="flex justify-between gap-4">
                {label}
                <span className="font-medium text-gray-900">{n}</span>
              </div>
            )}
          </div>
        )
      })}
    </dl>
  )
}

// fuente: 'excel' (an uploaded .xlsx) | 'buk' (fetched from Buk's API by the server)
function NominaModal({ fuente, personas, onClose, onApplied }) {
  const esBuk = fuente === 'buk'
  const { t } = useTranslation(['admin', 'common'])
  const [archivo, setArchivo] = useState(null)
  const [preview, setPreview] = useState(null)     // { filas, resumen }
  const [aplicado, setAplicado] = useState(null)   // { filas, resumen }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [inputKey, setInputKey] = useState(0)      // bump to clear the file input
  // Names for updated rows whose name didn't change (the response gives the id).
  const nombres = Object.fromEntries(personas.map(p => [p.id, p.name]))

  const enviar = async (aplicar) => {
    setBusy(true)
    setError('')
    try {
      const r = esBuk ? await sincronizarBuk(aplicar) : await importarNomina(archivo, aplicar)
      if (aplicar) {
        setAplicado(r)
        setPreview(null)
        setArchivo(null)
        setInputKey(k => k + 1)
        onApplied()
      } else {
        setPreview(r)
      }
    } catch (e) {
      setError(detalleError(e, t('personas.nomina.failed')))
    }
    setBusy(false)
  }

  return (
    <Modal title={t(esBuk ? 'personas.buk.title' : 'personas.nomina.title')} onClose={busy ? () => {} : onClose} wide>
      <div className="space-y-4">
        <div className="text-xs text-gray-500 space-y-1.5">
          <p>{t(esBuk ? 'personas.buk.intro' : 'personas.nomina.intro')}</p>
          <p>{t(esBuk ? 'personas.buk.privacidad' : 'personas.nomina.columnas')}</p>
          <ul className="list-disc ml-4 space-y-0.5">
            {['nombres', 'datos', 'nuevas', 'faltan', 'ambiguas'].map(r => (
              <li key={r}>{t(`personas.nomina.reglas.${r}`)}</li>
            ))}
          </ul>
        </div>

        {!esBuk && <input
          key={inputKey}
          type="file"
          accept=".xlsx"
          onChange={e => { setArchivo(e.target.files?.[0] || null); setPreview(null); setAplicado(null); setError('') }}
          className="block w-full text-sm text-gray-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-gray-100 file:text-sm file:text-gray-700 hover:file:bg-gray-200"
        />}

        {error && <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

        {!preview && !aplicado && (
          <div className="flex justify-end gap-2">
            <button onClick={onClose} disabled={busy}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">
              {t('common:actions.cancel')}
            </button>
            <button onClick={() => enviar(false)} disabled={(!esBuk && !archivo) || busy}
              className="bg-gray-900 text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors">
              {busy ? t('personas.nomina.working') : t('personas.nomina.preview')}
            </button>
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">{t('personas.nomina.previewIntro')}</p>
            <NominaResumen filas={preview.filas} resumen={preview.resumen} nombres={nombres} conFila={!esBuk} />
            <p className="text-xs text-gray-500">{t('personas.nomina.backupHint')}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPreview(null)} disabled={busy}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">
                {t('common:actions.cancel')}
              </button>
              <button onClick={() => enviar(true)} disabled={busy}
                className="bg-emerald-600 text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                {busy ? t('personas.nomina.working') : t('personas.nomina.apply')}
              </button>
            </div>
          </div>
        )}

        {aplicado && (
          <div className="space-y-3">
            <p className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
              {t('personas.nomina.done')}
            </p>
            <NominaResumen filas={aplicado.filas} resumen={aplicado.resumen} nombres={nombres} conFila={!esBuk} />
            <div className="flex justify-end">
              <button onClick={onClose}
                className="bg-gray-900 text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors">
                {t('personas.nomina.close')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function PersonaModal({ persona, areas, cargos, onClose, onSaved }) {
  const { personas: todas } = usePersonas()
  const { t } = useTranslation(['admin', 'common'])
  const { roleLabel } = useRoleText()
  const [form, setForm] = useState(() => persona ? {
    ...VACIO,
    name: persona.name, email: persona.email || '', phone: persona.phone || '', rut: persona.rut || '',
    role: persona.role || '', is_active: persona.is_active,
    areas: persona.areas.map(a => ({ area_id: a.area_id, es_jefe: a.es_jefe })),
    plans_hours: persona.plans_hours, max_weekly_hours: persona.max_weekly_hours,
    supervisor_id: persona.supervisor_id ?? null, profesion: persona.profesion || '',
    centro_costo: persona.centro_costo || '', fecha_nacimiento: persona.fecha_nacimiento || '',
    nacionalidad: persona.nacionalidad || '',
    disciplina: persona.disciplina || '', es_equipo: !!persona.es_equipo,
  } : VACIO)
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)
  const set = (campo) => (e) => setForm(f => ({ ...f, [campo]: e.target.value }))
  const cuenta = persona?.cuenta

  // Active areas, plus an inactive one this person still belongs to.
  const areasVisibles = areas.filter(a => a.is_active || form.areas.some(x => x.area_id === a.id))
  const enArea = (id) => form.areas.find(x => x.area_id === id)
  const toggleArea = (id) => setForm(f => ({
    ...f, areas: enArea(id) ? f.areas.filter(x => x.area_id !== id) : [...f.areas, { area_id: id, es_jefe: false }],
  }))
  const toggleJefe = (id) => setForm(f => ({
    ...f, areas: f.areas.map(x => x.area_id === id ? { ...x, es_jefe: !x.es_jefe } : x),
  }))

  const guardar = async (e) => {
    e.preventDefault()
    const email = form.email.trim().toLowerCase()
    if (!form.name.trim()) { setError(t('personas.form.nameRequired')); return }
    if (form.rut && !/^\d{1,8}[0-9K]$/.test(form.rut)) { setError(t('personas.form.rutInvalid')); return }
    if (form.crearCuenta) {
      if (!email.endsWith(DOMINIO)) { setError(t('personas.form.cuentaEmail', { dominio: DOMINIO })); return }
      if (!form.roles.length) { setError(t('personas.form.rolesRequired')); return }
    }
    const datos = {
      name: form.name.trim(),
      email: email || null,
      phone: form.phone.trim() || null,
      rut: form.rut || null,
      role: (form.role || '').trim() || null,
      is_active: form.is_active,
      plans_hours: form.is_active && form.plans_hours,
      max_weekly_hours: Number(form.max_weekly_hours) || 40,
      supervisor_id: form.supervisor_id ?? null,
      profesion: form.profesion.trim() || null,
      centro_costo: form.centro_costo.trim() || null,
      fecha_nacimiento: form.fecha_nacimiento || null,
      nacionalidad: form.nacionalidad.trim() || null,
      disciplina: form.disciplina || null,
      es_equipo: form.es_equipo,
      areas: form.areas,
    }
    setGuardando(true)
    setError('')
    try {
      if (!persona) {
        await createAdminPerson({ ...datos, ...(form.crearCuenta ? { cuenta: { roles: form.roles } } : {}) })
      } else {
        await updateAdminPerson(persona.id, datos)
        if (form.crearCuenta && !cuenta) await createAdminAccountFor(persona.id, form.roles)
      }
      onSaved()
    } catch (err) {
      setError(detalleError(err, t('personas.form.saveFailed')))
      setGuardando(false)
    }
  }

  return (
    <Modal title={persona ? t('personas.form.editTitle', { name: persona.name }) : t('personas.form.addTitle')} onClose={onClose} wide>
      <form onSubmit={guardar} className="space-y-5">
        {error && <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className={labelCls}>{t('personas.form.name')} *</label>
            <input autoFocus className={inputCls} value={form.name} onChange={set('name')} />
          </div>
          <div>
            <label className={labelCls}>{t('personas.form.email')}</label>
            <input type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder={`nombre${DOMINIO}`} />
          </div>
          <div>
            <label className={labelCls}>{t('personas.form.phone')}</label>
            <input className={inputCls} value={form.phone} onChange={set('phone')} />
          </div>
          <div>
            <label className={labelCls}>{t('personas.form.rut')}</label>
            {/* Filtered as typed: digits plus one K, only as the last character. */}
            <input maxLength={9} className={inputCls} value={form.rut} placeholder={t('personas.form.rutPlaceholder')}
              onChange={e => setForm(f => ({ ...f, rut: e.target.value.toUpperCase().replace(/[^0-9K]/g, '').replace(/K(?=.)/g, '').slice(0, 9) }))} />
          </div>
          <div>
            <label className={labelCls}>{t('personas.form.cargo')}</label>
            {/* HR's position, as text (the nómina keeps it); suggestions from the
                positions already in use. What Capacity groups by is the discipline below. */}
            <input className={inputCls} value={form.role} onChange={set('role')} list="cargos-sugeridos"
              placeholder={t('personas.form.sinCargo')} />
            <datalist id="cargos-sugeridos">
              {[...new Set([...cargos, ...todas.map(p => p.role).filter(Boolean)])].sort().map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>{t('personas.form.supervisor')}</label>
            {/* Who they report to: their leave is approved up this line. */}
            <PersonaPicker value={form.supervisor_id}
              personas={todas.filter(p => p.id !== persona?.id)}
              placeholder={t('personas.form.sinSupervisor')}
              onChange={id => setForm(f => ({ ...f, supervisor_id: id ?? null }))} />
            <p className="text-[11px] text-gray-400 mt-1">{t('personas.form.supervisorHint')}</p>
          </div>
          <div>
            <label className={labelCls}>{t('personas.form.profesion')}</label>
            <input className={inputCls} value={form.profesion} onChange={set('profesion')} />
          </div>
          <div>
            <label className={labelCls}>{t('personas.form.centroCosto')}</label>
            <input className={inputCls} value={form.centro_costo} onChange={set('centro_costo')} />
          </div>
          <div>
            <label className={labelCls}>{t('personas.form.fechaNacimiento')}</label>
            <input type="date" className={inputCls} value={form.fecha_nacimiento} onChange={set('fecha_nacimiento')} />
          </div>
          <div>
            <label className={labelCls}>{t('personas.form.nacionalidad')}</label>
            <input className={inputCls} value={form.nacionalidad} onChange={set('nacionalidad')} />
          </div>
        </div>
        <p className="text-[11px] text-gray-400 -mt-2">{t('personas.form.privadosHint')}</p>

        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" className="mt-0.5 accent-gray-900" checked={form.is_active}
            onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
          <span>
            {t('personas.form.isActive')}
            <span className="block text-xs text-gray-400">{t('personas.form.isActiveHint')}</span>
          </span>
        </label>

        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('personas.form.areas')}</h3>
          {!areasVisibles.length ? (
            <p className="text-xs text-gray-400">{t('personas.form.sinAreas')}</p>
          ) : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {areasVisibles.map(a => {
                const fila = enArea(a.id)
                return (
                  <div key={a.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" className="accent-gray-900" checked={!!fila} onChange={() => toggleArea(a.id)} />
                      {a.name}
                      {!a.is_active && <span className="text-[10px] text-gray-400">({t('areas.inactiva')})</span>}
                    </label>
                    <label title={t('personas.form.jefeHint')}
                      className={`flex items-center gap-1.5 text-xs ${fila ? 'text-gray-600 cursor-pointer' : 'text-gray-300'}`}>
                      <input type="checkbox" className="accent-indigo-600" disabled={!fila} checked={!!fila?.es_jefe}
                        onChange={() => toggleJefe(a.id)} />
                      {t('personas.form.jefe')}
                    </label>
                  </div>
                )
              })}
            </div>
          )}
          {areasVisibles.length > 0 && (
            <p className="text-xs text-gray-400 mt-1.5">{t('personas.form.jefeHint')}</p>
          )}
        </section>

        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('personas.form.ingenieria')}</h3>
          <div className="flex flex-wrap items-end gap-6">
            <label className={`flex items-start gap-2 text-sm ${form.is_active ? 'text-gray-700' : 'text-gray-300'}`}>
              <input type="checkbox" className="mt-0.5 accent-gray-900" disabled={!form.is_active}
                checked={form.is_active && form.plans_hours}
                onChange={e => setForm(f => ({ ...f, plans_hours: e.target.checked }))} />
              <span>
                {t('personas.form.plansHours')}
                <span className="block text-xs text-gray-400">{t('personas.form.plansHoursHint')}</span>
              </span>
            </label>
            <div>
              <label className={labelCls}>{t('personas.form.maxHours')}</label>
              <input type="number" min="1" max="80" className={`${inputCls} w-28`} value={form.max_weekly_hours}
                onChange={set('max_weekly_hours')} />
            </div>
            <div>
              <label className={labelCls}>{t('personas.form.disciplina')}</label>
              <select className={`${inputCls} w-48`} value={form.disciplina} onChange={set('disciplina')}>
                <option value="">{t('personas.form.sinDisciplina')}</option>
                {DISCIPLINAS.map(d => <option key={d} value={d}>{t(`personas.disciplinas.${d}`)}</option>)}
              </select>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">{t('personas.form.disciplinaHint')}</p>
          <label className="flex items-start gap-2 text-sm text-gray-700 mt-3">
            <input type="checkbox" className="mt-0.5 accent-gray-900" checked={form.es_equipo}
              onChange={e => setForm(f => ({ ...f, es_equipo: e.target.checked }))} />
            <span>
              {t('personas.form.esEquipo')}
              <span className="block text-xs text-gray-400">{t('personas.form.esEquipoHint')}</span>
            </span>
          </label>
        </section>

        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('personas.form.cuenta')}</h3>
          {cuenta ? (
            <div className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-gray-700">{cuenta.email || '—'}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CUENTA_BADGE[estadoCuenta(cuenta)]}`}>
                  {t(`personas.cuentaEstado.${estadoCuenta(cuenta)}`)}
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {cuenta.roles.map(r => (
                  <span key={r} className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge(r)}`}>{roleLabel(r)}</span>
                ))}
              </div>
              <Link to="/admin/cuentas" className="inline-block text-xs font-medium text-gray-500 hover:text-gray-900 underline">
                {t('personas.form.verCuentas')}
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="checkbox" className="mt-0.5 accent-gray-900" checked={form.crearCuenta}
                  onChange={e => setForm(f => ({ ...f, crearCuenta: e.target.checked }))} />
                <span>
                  {t('personas.form.crearCuenta')}
                  <span className="block text-xs text-gray-400">{t('personas.form.crearCuentaHint', { dominio: DOMINIO })}</span>
                </span>
              </label>
              {form.crearCuenta && (
                <RolesChecklist value={form.roles} onChange={roles => setForm(f => ({ ...f, roles }))} />
              )}
            </div>
          )}
        </section>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">
            {t('common:actions.cancel')}
          </button>
          <button type="submit" disabled={guardando}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
            {guardando ? t('common:actions.saving') : t('common:actions.save')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
